import { makeAutoObservable, runInAction } from 'mobx';

import { cmApi } from '@src/api';
import { preloaderStore } from '@stores/preloaderStore';
import type { CmApi, ReportSettingDto } from '@src/api';

import { reportSettingsStore } from '../reportSettingsStore';

class ReportSettingEditStore {
  reportSetting: ReportSettingDto | null;

  isNew = false;

  editMode = false;

  isLoading = false;

  api: CmApi;

  hiddenEdit = false;

  hiddenDelete = false;

  hiddenClean = false;

  hiddenSave = false;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    this.reportSetting = null;

    makeAutoObservable(this);
  }

  get isEditMode() {
    return this.editMode || this.isNew;
  }

  setHiddenEdit = (hiddenEdit = true): void => {
    this.hiddenEdit = hiddenEdit;
  };

  setHiddenDelete = (hiddenDelete = true): void => {
    this.hiddenDelete = hiddenDelete;
  };

  setHiddenClean = (hiddenClean = true): void => {
    this.hiddenClean = hiddenClean;
  };

  setHiddenSave = (hiddenSave = true): void => {
    this.hiddenSave = hiddenSave;
  };

  setNew = (isNew = true) => {
    this.isNew = isNew;
    this.reportSetting = null;
  };

  setEditMode = (editMode = true) => {
    this.editMode = editMode;
  };

  save = async (reportSetting: ReportSettingDto) => {
    preloaderStore.preloader(true);
    const { data } = await (this.isNew
      ? this.api.reportSettings.addReportSettings(reportSetting)
      : this.api.reportSettings.updateReportSettings(reportSetting));

    if (data?.reportVersionCode) {
      reportSettingsStore.setIsChanged(true);

      if (this.isNew) {
        preloaderStore.preloader(false);
        return { reportVersionCode: data.reportVersionCode };
      }
      runInAction(() => {
        this.reportSetting = data;
        this.setEditMode(false);
      });
    }
    preloaderStore.preloader(false);
    return undefined;
  };

  delete = async () => {
    if (this.reportSetting?.reportVersionCode) {
      const { organizationCode } = reportSettingsStore;

      await this.api.reportSettings.deleteReportSettings([
        { organizationCode, reportVersionCode: this.reportSetting?.reportVersionCode },
      ]);
      reportSettingsStore.setIsChanged(true);
    }
  };

  fetch = async (reportVersionCode: string): Promise<void> => {
    const { organizationCode } = reportSettingsStore;

    if (!reportVersionCode) {
      return;
    }
    preloaderStore.preloader(true);
    runInAction(() => {
      this.isLoading = true;
      this.setEditMode(false);
      this.setNew(false);
    });

    const { data } = await this.api.reportSettings.getReportSetting(organizationCode, reportVersionCode);

    runInAction(() => {
      this.reportSetting = data;
      this.isLoading = false;
    });
    preloaderStore.preloader(false);
  };
}

export const reportSettingEditStore = new ReportSettingEditStore({ api: cmApi });
